/**
 * useTrialVideoQuota
 *
 * Espelha no front a regra que o backend já aplica em
 * `e-video-vsl-be/middleware/validateUserInTrialingPeriodForSendVideo.js`:
 * durante o período de teste da assinatura, o usuário pode ter no máximo
 * 1 VSL normal e 3 VSL dinâmicas (limites independentes).
 *
 * O objetivo é abrir o paywall no MESMO momento em que a API recusaria, e não
 * criar uma trava nova — por isso todo desvio da regra do middleware aqui é bug.
 *
 * ATENÇÃO: isto é UX, não segurança. A autoridade continua sendo o middleware;
 * quem chamar a API direto passa por cima deste hook.
 */

import { useQuery } from '@tanstack/react-query';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { videoService } from '@/services/videoService';
import { userService } from '@/services/userService';
import { cupomService, StripeCouponData } from '@/services/cupomService';

// Os números vivem hardcoded no middleware (linha do `excedeLimite`); não há env
// var nem tabela de config no backend. Se mudarem lá, mudam aqui.
export const TRIAL_MAX_VSL_NORMAL = 1;
export const TRIAL_MAX_VSL_DINAMICA = 3;

interface SubscriptionRaw {
  status?: string;
  cupom?: string | null;
}

/**
 * "Período de teste" é ambíguo neste produto e os dois sentidos NÃO se
 * sobrepõem:
 *
 *  - `plan.periodo_teste === true` / `plan.status === 'trial'` — usuário que
 *    nunca assinou nada (cai no "Teste Gratuito" de 300 clicks). O middleware
 *    NÃO limita esse caso: sem doc em `Subscription`, o status é `undefined` e
 *    o upload passa livre.
 *  - `plan.status === 'trialing'` — assinatura real do Stripe dentro dos 15
 *    dias grátis (`trial_period_days: 15` no checkout). É ESTE que o middleware
 *    bloqueia, e portanto é o único que deve ver o paywall.
 */
const isTrialingPlan = (status?: string) => status === 'trialing';

export function useTrialVideoQuota() {
  const { user, plan } = useCustomAuth();

  const isTrialing = isTrialingPlan(plan?.status);
  const userId = user?.id_user;

  // Contagem de VSLs normais. `includeFolderVideos` é obrigatório: sem ele o
  // backend exclui os vídeos que estão dentro de pastas, e quem guardou o único
  // vídeo numa pasta apareceria com total 0 — o paywall nunca abriria e o
  // usuário levaria o erro cru da API.
  const {
    data: videosData,
    isLoading: isLoadingVideos,
    refetch: refetchCount,
  } = useQuery({
    queryKey: ['trial-quota', 'videos-count', userId],
    queryFn: () =>
      videoService.getVideosList({ page: 1, limit: 1, includeFolderVideos: true }),
    enabled: !!userId && isTrialing,
    staleTime: 30 * 1000,
  });

  // O cupom vive só em `Subscription.cupom` (não há campo equivalente no
  // usuário). Mesma queryKey do useUserPlan de propósito, para as duas telas
  // compartilharem o cache desta chamada.
  const { data: userPlanData } = useQuery({
    queryKey: ['userPlan', userId],
    queryFn: () => userService.getPlan(userId as string),
    enabled: !!userId && isTrialing,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const subs: SubscriptionRaw[] = userPlanData?.data?.sub ?? [];
  const assinaturaVigente = subs.find((s) =>
    ['trialing', 'active'].includes(s.status || '')
  );
  const cupomCode = assinaturaVigente?.cupom || undefined;

  // Os dados do cupom vêm do STRIPE (POST /cupom faz coupons.retrieve), que é a
  // única fonte autoritativa de percent_off/duration. Não calculamos desconto a
  // partir de constante local: se o cupom mudar no painel, a tela acompanha.
  const { data: cupomResponse } = useQuery({
    queryKey: ['trial-quota', 'cupom', cupomCode],
    queryFn: () => cupomService.validate(cupomCode as string),
    enabled: !!cupomCode && isTrialing,
    staleTime: 10 * 60 * 1000,
  });

  const cupom: StripeCouponData | undefined =
    cupomResponse?.success && cupomResponse.data?.valid !== false
      ? cupomResponse.data
      : undefined;

  const totalVslNormais = videosData?.pagination?.total ?? 0;

  /**
   * A contagem do dashboard não é idêntica à do middleware — ele conta TODOS os
   * documentos do usuário (clones de speed/autoplay/headline test inclusive),
   * enquanto `/videos/dashboard/list` filtra esses clones. Com o limite em 1 os
   * dois convergem: basta existir uma VSL normal para ambos bloquearem. Se o
   * limite subir, esta contagem passa a ser otimista e o usuário veria o erro
   * cru da API em vez do paywall.
   */
  const atingiuLimiteVslNormal = isTrialing && totalVslNormais >= TRIAL_MAX_VSL_NORMAL;

  return {
    isTrialing,
    totalVslNormais,
    atingiuLimiteVslNormal,
    limiteVslNormal: TRIAL_MAX_VSL_NORMAL,
    limiteVslDinamica: TRIAL_MAX_VSL_DINAMICA,
    cupomCode,
    cupom,
    isLoading: isLoadingVideos,
    refetchCount,
  };
}

export default useTrialVideoQuota;
